/**
 * Comment anchor resolution.
 *
 * PM positions are per-editor-instance. We use stored positions as the fast
 * path and fall back to searching `quotedText` inside the section on mismatch
 * (handles drift when text was edited since the comment was created).
 *
 * `resolveAnchor` is the single resolution path — the editor's `comment:jump`
 * listener calls it too, so scroll targets and painted highlights can never
 * disagree.
 *
 * `jumpToComment` scrolls the editor to the anchor and emits a flash. It is
 * intentionally loose-coupled to the editor via window dispatching so the
 * Comments tab doesn't have to hold a ref to the TipTap editor.
 */

import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import type { Comment } from '@/lib/comments-api'

export interface AnchorRange {
  from: number
  to: number
}

/**
 * Everything resolution reads is the document. A TipTap `Editor` satisfies this
 * structurally, and so does `{ state: { doc: tr.doc } }` — which is what the
 * comment decoration plugin has inside `apply()`, where no `Editor` exists.
 */
export interface AnchorDocSource {
  state: { doc: ProseMirrorNode }
}

/**
 * The only fields anchor resolution reads. A full `Comment` satisfies it, and
 * so does the `comment:jump` event detail — one resolution path, no cast.
 */
export type CommentAnchor = Pick<
  Comment,
  'sectionId' | 'startPos' | 'endPos' | 'quotedText'
>

/**
 * Walk the doc and find the [from, to] range covered by a given sectionId.
 * Relies on segmentHeading nodes delimiting sections (see SegmentHeading.ts).
 */
export function findSectionRange(
  editor: AnchorDocSource,
  sectionId: string,
): AnchorRange | null {
  let from: number | null = null
  let to: number | null = null
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'segmentHeading') return
    const attrs = node.attrs as { sectionId?: string | null }
    if (attrs.sectionId === sectionId) {
      from = pos + node.nodeSize
    } else if (from !== null && to === null) {
      to = pos
      return false
    }
    return
  })
  if (from == null) return null
  if (to == null) to = editor.state.doc.content.size
  return { from, to }
}

export function resolveAnchor(
  editor: AnchorDocSource,
  comment: CommentAnchor,
): AnchorRange | null {
  if (comment.sectionId == null) return null
  // For single-document artifacts there are no segmentHeading delimiters;
  // fall back to the whole doc.
  const section =
    findSectionRange(editor, comment.sectionId) ??
    { from: 0, to: editor.state.doc.content.size }

  if (
    comment.startPos != null &&
    comment.endPos != null &&
    comment.quotedText != null
  ) {
    const clampedFrom = Math.max(section.from, Math.min(comment.startPos, section.to))
    const clampedTo = Math.max(clampedFrom, Math.min(comment.endPos, section.to))
    if (clampedTo > clampedFrom) {
      const currentText = editor.state.doc.textBetween(clampedFrom, clampedTo, ' ')
      if (currentText === comment.quotedText) {
        return { from: clampedFrom, to: clampedTo }
      }
    }
  }

  const needle = comment.quotedText
  if (needle) {
    // Text offsets are NOT PM positions — every node boundary costs 2 position
    // tokens that `textBetween` collapses into 0-or-1 characters, so
    // `section.from + indexOf(...)` drifts left once the section holds more
    // than one text node. Walk the text nodes instead and keep a map from
    // concatenated-text offsets back to real PM positions.
    //
    // Trade-off: the runs are concatenated with no separator, so a quote that
    // spans two blocks no longer matches (it was mis-ranged before anyway).
    let acc = ''
    const runs: Array<{ textStart: number; pmStart: number; len: number }> = []
    editor.state.doc.nodesBetween(section.from, section.to, (node, pos) => {
      if (!node.isText || !node.text) return
      // Clip to the section in case a text node straddles the boundary.
      const nodeFrom = Math.max(pos, section.from)
      const nodeTo = Math.min(pos + node.text.length, section.to)
      if (nodeTo <= nodeFrom) return
      const text = node.text.slice(nodeFrom - pos, nodeTo - pos)
      runs.push({ textStart: acc.length, pmStart: nodeFrom, len: text.length })
      acc += text
    })

    const idx = acc.indexOf(needle)
    if (idx >= 0) {
      // At a run boundary the start binds to the following run (so a match
      // opening a block lands inside it) and the end binds to the preceding
      // run (so a match closing a block stops before its closing token).
      const toPmStart = (offset: number): number => {
        for (const run of runs) {
          if (offset < run.textStart + run.len) {
            return run.pmStart + (offset - run.textStart)
          }
        }
        return section.to
      }
      const toPmEnd = (offset: number): number => {
        for (const run of runs) {
          if (offset <= run.textStart + run.len) {
            return run.pmStart + (offset - run.textStart)
          }
        }
        return section.to
      }
      const from = toPmStart(idx)
      const to = toPmEnd(idx + needle.length)
      if (to > from) return { from, to }
    }
  }

  return null
}

/**
 * Emit a dom event on `window` that the DraftEditor listens for to scroll
 * + flash the anchor. The editor subscribes with an effect so the anchor
 * logic can be tested in isolation.
 */
export function jumpToComment(comment: Comment): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent('comment:jump', {
      detail: {
        commentId: comment.id,
        sectionId: comment.sectionId,
        startPos: comment.startPos,
        endPos: comment.endPos,
        quotedText: comment.quotedText,
      },
    }),
  )
}
